import { Currency } from "@redotech/money/currencies";
import { WidgetFees } from "@redotech/redo-storefront-widget-common/widget";
import {
  DeliveryType,
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartAttributes,
  ShopifyCartChange,
  ShopifyCartItem,
  ShopifyClientType,
  ShopifyProduct,
  ShopifyProductJS,
  ShopifyShop,
} from "@redotech/shopify-client/ajax";
import { log } from "../monitoring/log";

/**
 * Commerce Cloud (SFCC) StoreFront Client
 * Implements ExtensionShopifyClient interface for Salesforce Commerce Cloud integration
 */
export class LightopiaStorefrontClient implements ExtensionShopifyClient {
  type = ShopifyClientType.LIGHTOPIA;

  async cartAdd(
    data: { items: Partial<ShopifyCartItem>[] },
    params?: URLSearchParams,
    preventReload?: boolean,
  ): Promise<void> {
    for (const item of data.items) {
      const iwNumber = String(item.variant_id || item.id || "");
      const quantity = String(item.quantity || 1);

      const body = new URLSearchParams();
      body.append("RecommendedItemIW", "");
      body.append("Items[0][IWNumber]", iwNumber);
      body.append("Items[0][Quantity]", quantity);

      await fetch("/Cart/AddToCart2", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
    }
  }

  async cartGet(signal?: AbortSignal): Promise<ShopifyCart> {
    const response = await fetch("/Cart/ViewCart", { signal });

    const parser = new DOMParser();
    const doc = parser.parseFromString(await response.text(), "text/html");

    const items: ShopifyCartItem[] = [];

    for (const cartItemEl of doc.querySelectorAll(".cart-item")) {
      const anyInput = cartItemEl.querySelector(
        "input[name]",
      ) as HTMLInputElement | null;
      if (!anyInput) {
        continue;
      }

      // Extract prefix like "Items[0]" or "RelItems[0]"
      const prefix = anyInput.name.replace(/\.[^.]+$/, "");

      const getHiddenVal = (field: string) =>
        (
          cartItemEl.querySelector(
            `input[name="${prefix}.${field}"]`,
          ) as HTMLInputElement | null
        )?.value ?? "";

      const iwNumber = getHiddenVal("IWNumber");
      const itemId = getHiddenVal("ItemId");
      const prodName = getHiddenVal("ProdName");
      const prodBrand = getHiddenVal("ProdBrand");
      const quantity = parseInt(getHiddenVal("Quantity"), 10) || 0;

      if (!itemId) {
        continue;
      }

      let price = parseFloat(getHiddenVal("ProdPrice"));
      if (isNaN(price)) {
        // RelItems don't have a ProdPrice input — parse from the visible ".price" span
        const priceText =
          (cartItemEl.querySelector(".price") as HTMLElement | null)
            ?.textContent ?? "";
        price = parseFloat(priceText.replace(/[^0-9.]/g, "")) || 0;
      }

      items.push({
        id: iwNumber,
        line_id: itemId || iwNumber,
        product_id: itemId,
        variant_id: iwNumber || itemId,
        handle: iwNumber || itemId,
        title: prodName || iwNumber,
        vendor: prodBrand,
        quantity,
        requires_shipping: !iwNumber?.startsWith("PP-"),
        line_price: Math.round(price * quantity * 100),
        properties: null,
        sku: iwNumber,
      });
    }

    if (items.some((item) => item.line_price === 0)) {
      log("WARN: Item has line price of 0");
    }

    const total_price = items.reduce(
      (sum, item) => sum + (item.line_price ?? 0),
      0,
    );

    const cart = {
      attributes: {},
      items,
      currency: Currency.USD,
      total_price,
      delivery_type: DeliveryType.SHIPPING,
    };
    return cart;
  }

  async cartChange(
    change: ShopifyCartChange,
    signal?: AbortSignal,
  ): Promise<void> {
    const prefix = "Items[0]";

    const body = new URLSearchParams([
      [`${prefix}.IWNumber`, String(change.id || "")],
      [`${prefix}.ItemId`, String(change.line?.toString() || "")],
      [`${prefix}.QtyAvailable`, "0"],
      [`${prefix}.QuantityInit`, String(change.quantity)],
      [`${prefix}.ProdVar`, ""],
      [`${prefix}.Quantity`, String(change.quantity)],
      [`${prefix}.HasChange`, "true"],
    ]);

    await fetch("/Cart/ViewCart", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
      signal,
    });
  }

  async removeItemFromCart(
    item: ShopifyCartItem,
    numToRemove: number,
    signal?: AbortSignal,
  ): Promise<undefined> {
    const prefix = "Items[0]";
    const newQuantity = Math.max(0, item.quantity - numToRemove);

    const body = new URLSearchParams([
      [`${prefix}.IWNumber`, String(item.variant_id || "")],
      [`${prefix}.ItemId`, String(item.product_id || item.id || "")],
      [`${prefix}.QtyAvailable`, "0"],
      [`${prefix}.QuantityInit`, String(item.quantity)],
      [
        `${prefix}.ProdPrice`,
        String(((item.line_price ?? 0) / item.quantity / 100).toFixed(2)),
      ],
      [`${prefix}.ProdVar`, ""],
      [`${prefix}.Quantity`, String(newQuantity)],
      [`${prefix}.HasChange`, "true"],
    ]);

    await fetch("/Cart/ViewCart", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
      signal,
    });
  }

  async cartClear(): Promise<void> {
    return;
  }

  async updateCartAttribute(
    attributes: ShopifyCartAttributes,
    signal?: AbortSignal,
  ): Promise<ShopifyCart | void> {
    log("updateCartAttribute() was called, but is unsupported for lightopia.");
    return;
  }

  async updateCartItemSellingPlan(
    item: ShopifyCartItem,
    sellingPlanId: number | null,
    signal?: AbortSignal,
  ): Promise<undefined> {
    log(
      "updateCartItemSellingPlan() was called, but is unsupported for Lightopia.",
    );
  }

  async getCollections(
    handle?: string,
    signal?: AbortSignal,
  ): Promise<{ title: string; handle: string }[] | undefined> {
    log("getCollections() was called, but is unsupported for Lightopia.");

    return [];
  }

  async productGet(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProduct> {
    // Lightopia has no product tag API — return a stub so eligibility checks pass
    throw new Error("Unsupported");
  }

  async productGetJS(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProductJS> {
    throw new Error("Unsupported");
  }

  async getShopMetafield(
    data: { key: string; namespace: string },
    signal?: AbortSignal,
  ): Promise<ShopifyShop | undefined> {
    throw new Error("Unsupported");
  }

  async deleteCartAttribute(
    data: { key: string },
    signal?: AbortSignal,
  ): Promise<void> {
    log(
      "deleteCartAttribute() was called, but is unsupported for Commerce Cloud.",
    );
  }

  static getProductJsFromFees = ({
    fees,
  }: {
    fees: WidgetFees;
  }): ShopifyProductJS => {
    const mainProduct = {
      return: fees.returnProduct,
      package_protection: fees.packageProduct,
      "return,package_protection": fees.bothProduct,
    }[fees.mainType];

    if (!mainProduct) {
      throw new Error("Main product not found");
    }

    return {
      id: mainProduct.productId,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      price_min: Number(mainProduct!.variants![0]!.price) * 100,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      price_max: Number(mainProduct!.variants![0]!.price) * 100,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      variants: mainProduct!.variants!.map((variant) => ({
        id: variant.variant_id,
        price: Number(variant.price) * 100,
        requires_shipping: true,
      })),
    };
  };

  static isOnCheckoutPage(): boolean {
    return window.location.pathname.includes("/checkout");
  }
}
